import React, { ReactElement, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { partTestId } from '@connect-x-app/shared-utils';
import { arrow, autoUpdate, FloatingArrow, offset, Placement, ReferenceType, shift, useFloating } from '@floating-ui/react';

import './Tooltip.scss';

type Slot = ReactElement<{ name: 'header' | 'content' }>;

type Props = {
  alignment: Placement;
  anchor: ReferenceType | null;
  children: Slot | Slot[];
  testId?: string;
};

export const Tooltip = (props: Props) => {
  const { alignment, anchor, children, testId } = props;

  const arrowRef = useRef<SVGSVGElement>(null);
  const { context, floatingStyles, refs } = useFloating({
    middleware: [
      offset({
        mainAxis: 10,
      }),
      shift({
        padding: 15,
      }),
      arrow({
        element: arrowRef,
        padding: 5,
      }),
    ],
    placement: alignment,
    whileElementsMounted: autoUpdate,
  });

  const content: Slot[] = [];
  const header: [Slot?] = [];

  useEffect(() => {
    refs.setReference(anchor);
  }, [anchor, refs]);

  if (!anchor) {
    return null;
  }

  const empty: Slot[] = [];

  empty.concat(children).forEach((child: Slot) => {
    if (!React.isValidElement(child)) {
      return;
    }
    if (child.type !== 'slot') {
      return;
    }

    switch (child.props.name) {
      case 'header':
        header.push(child);
        break;
      case 'content':
        content.push(child);
        break;
      default:
        break;
    }
  });

  return createPortal(
    <div className="tooltip" data-test={testId} ref={refs.setFloating} style={floatingStyles}>
      {header.length === 1 ? (
        <p className="tooltip-header" data-test={partTestId(testId, 'header')}>
          {header.at(0)}
        </p>
      ) : null}
      {content.map((contentSlot, index) => {
        return (
          <section className="tooltip-content" data-test={partTestId(testId, 'content')} key={index}>
            {contentSlot}
          </section>
        );
      })}
      <FloatingArrow className="tooltip-arrow" context={context} data-test={partTestId(testId, 'arrow')} ref={arrowRef} />
    </div>,
    document.body,
  );
};
